import { curveLength, shapeSimilarity } from 'curve-matcher'

import { MATCH_THRESHOLD } from '@/lib/grader/threshold'
import type { GraderResult, Stroke } from '@/lib/grader/types'

/**
 * Grade a drawing against the KNOWN target (§3.1) — not classification.
 * Matches stroke-for-stroke when stroke counts agree; otherwise scores the
 * best alignment and penalises the count mismatch. shapeSimilarity uses
 * Procrustes + Fréchet; checkRotations:false keeps orientation strict.
 *
 * API note: curve-matcher v1.1.1 uses `checkRotations` (not `rotationInvariant`).
 * `checkRotations: false` is the correct way to disable rotation invariance.
 */
export function gradeDrawing(
    userStrokes: Stroke[],
    targetStrokes: Stroke[]
): GraderResult {
    if (userStrokes.length === 0 || targetStrokes.length === 0) {
        return { score: 0, accepted: false, perStroke: [] }
    }

    const pairCount = Math.min(userStrokes.length, targetStrokes.length)
    const perStroke: number[] = []
    let total = 0
    for (let i = 0; i < pairCount; i++) {
        const u = userStrokes[i]!
        const t = targetStrokes[i]!
        if (curveLength(u) === 0 || curveLength(t) === 0) {
            perStroke.push(0)
            continue
        }
        const s = shapeSimilarity(u, t, { checkRotations: false })
        perStroke.push(s)
        total += s
    }

    // Penalise stroke-count mismatch: divide by the larger count so missing or
    // extra strokes drag the mean down.
    const denom = Math.max(userStrokes.length, targetStrokes.length)
    const score = total / denom

    return { score, accepted: score >= MATCH_THRESHOLD, perStroke }
}
